import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IDiagnosisTermItem } from '@common/interfaces/api/diagnosisTermItem';
import { AbortableFetch } from '@frontend/store/fetch';
import type { IReduxState } from '../../../../../../configureStore';
import { translateKey } from '../../../../../../translate';
import { DropdownSearch } from '../../../../../elements/common';
import { DropDownAsyncSearchHelper } from '../dropDownAsyncSearchHelper';

interface IIcd10SelectionProps {
    bold?: boolean;
    error?: string;
    icd10Code: string | null;
    id?: string;
    isWorkingDiagnosis?: boolean;
    onChange: (icd10Code: string | null, thesaurusTermId: string | null, icd10CodeFallBack?: string | null) => void;
    onDoValidate: () => void;
    placeholder?: string;
    specialismCode: string;
    termId: string | null;
}

export const NODIAGNOSISYET = 'R69';
export const NOABNORMALITY = 'R68.8';

export function Icd10Selection(props: IIcd10SelectionProps) {
    let abortableFetch = new AbortableFetch();
    const { icd10Code, termId, specialismCode, placeholder, error } = props;

    const languageCode = useSelector((state: IReduxState) => state.language.code);
    const [searchHelper, setSearchHelper] = useState(false);

    const [diagnosisTermsAndCodes, setDiagnosisTermsAndCodes] = useState<IDiagnosisTermItem[]>([]);
    const [selectedOption, setSelectedOption] = useState<IDiagnosisTermItem | undefined>(undefined);

    const [showSecondDropdown, setShowSecondDropdown] = useState(false);
    const [nestedOptions, setNestedOptions] = useState<IDiagnosisTermItem[] | undefined>(undefined);

    const [errorFirstDropdown, setErrorFirstDropdown] = useState<string | undefined>(undefined);

    const valueFirstDropdown = selectedOption ? `${selectedOption?.code || ''}#${selectedOption?.termId || ''}` : null;
    const valueSecondDropdown = `${icd10Code || ''}#${termId || ''}`;

    useEffect(() => {
        if (!selectedOption && diagnosisTermsAndCodes && (icd10Code || termId)) {
            setSelectedOption(findSelectedByCodeAndTermId(icd10Code || '', termId || ''));
        }
    }, [icd10Code, termId, diagnosisTermsAndCodes, selectedOption]);

    useEffect(() => {
        if (selectedOption && !icd10Code && !termId) {
            resetSelection();
        }
    }, [icd10Code, termId]);

    useEffect(() => {
        setNestedOptions(selectedOption?.thesaurusTerms);
    }, [selectedOption]);

    useEffect(() => {
        if (selectedOption && selectedOption?.code && !icd10Code) {
            onChangeIcd10Fallback(selectedOption?.code);
        }
    }, [icd10Code]);

    useEffect(() => {
        setShowSecondDropdown(nestedOptions !== undefined && nestedOptions.length > 0);
        const icd10CodeFallBack = nestedOptions ? nestedOptions[0].code : null;
        onChangeIcd10Fallback(icd10CodeFallBack);
    }, [nestedOptions]);

    useEffect(() => {
        if (error && !selectedOption) {
            setErrorFirstDropdown(error);
        } else {
            setErrorFirstDropdown(undefined);
        }
    }, [error, selectedOption]);

    useEffect(() => {
        return () => abortableFetch.abort();
    }, []);

    return (
        <div id={props.id}>
            <DropdownSearch
                error={errorFirstDropdown}
                loadOptions={async (value) => await loadOptions(value)}
                noOptionsMessage={searchHelper ? <DropDownAsyncSearchHelper /> : undefined}
                onChange={(value) => onChange(value)}
                onDoValidate={async () => {
                    await props.onDoValidate();
                }}
                placeholder={placeholder ? placeholder : translateKey('diagnosis.selectOrTypeATerm')}
                value={valueFirstDropdown}
            />

            {showSecondDropdown && (
                <DropdownSearch
                    loadOptions={async () => await getOptionsSecondDropdown()}
                    noOptionsMessage={searchHelper ? <DropDownAsyncSearchHelper /> : undefined}
                    onChange={(value) => onChangeSecondDropdown(value)}
                    onDoValidate={async () => {
                        await props.onDoValidate();
                    }}
                    placeholder={translateKey('diagnosis.specifyDiagnosis')}
                    value={valueSecondDropdown}
                />
            )}
        </div>
    );

    function onChange(selectedValue: string | null) {
        if (selectedValue === null) {
            resetSelection();
        } else {
            const [selectedValuecode, selectedValuetermId] = selectedValue.split('#');
            props.onChange(selectedValuecode || null, selectedValuetermId || null);
            setSelectedOption(findSelectedByCodeAndTermId(selectedValuecode, selectedValuetermId));
        }
    }

    function resetSelection() {
        props.onChange(null, null, null);
        setSelectedOption(undefined);
        setDiagnosisTermsAndCodes([]);
    }

    function onChangeSecondDropdown(selectedValue: string | null) {
        if (selectedValue === null) {
            props.onChange(null, termId);
        } else {
            const [selectedValuecode, selectedValuetermId] = selectedValue.split('#');
            props.onChange(selectedValuecode, selectedValuetermId || null);
        }
    }

    async function onChangeIcd10Fallback(icd10CodeFallBack?: string | null) {
        await props.onChange(icd10Code, termId, icd10CodeFallBack);
        await props.onDoValidate();
    }

    async function loadOptions(input?: string) {
        const options: { label: string; value: string }[] = [];
        const baseUrl = `/api/dhdThesaurus/thesaurusTerms/?specialismCode=${specialismCode}&languageCode=${languageCode}&limit=100`;
        const hasSelectedOption =
            termId || (icd10Code && icd10Code !== '-' && icd10Code !== NODIAGNOSISYET && icd10Code !== NOABNORMALITY);
        let urlSelectedValue;

        if (termId) {
            urlSelectedValue = baseUrl + `&termId=${termId}`;
        } else if (icd10Code && icd10Code !== '-' && icd10Code !== NODIAGNOSISYET && icd10Code !== NOABNORMALITY) {
            urlSelectedValue = baseUrl + `&code=${encodeURIComponent(icd10Code)}`;
        }

        if (urlSelectedValue) {
            const result = await abortableFetch.fetchJson(urlSelectedValue, 'GET');
            setDiagnosisTermsAndCodes(result.json.items);

            if (result.json.items && result.json.items.length > 0) {
                options.push({
                    label: result.json.items[0].description,
                    value: result.json.items[0].code + '#' + result.json.items[0].termId,
                });
            }
            if (props.isWorkingDiagnosis) {
                options.push(
                    { label: translateKey('diagnosis.noDiagnosisYet'), value: NODIAGNOSISYET },
                    { label: translateKey('diagnosis.noAbnormality'), value: NOABNORMALITY }
                );
            }
        }

        if (input && input.length >= 2) {
            cancelPreviousFetching();
            const url = baseUrl + `&descriptionSearch=${input}`;
            const result = await abortableFetch.fetchJson(url, 'GET');
            result.json.items.forEach((item: { code: string; description: string; termId: string }) => {
                options.push({ label: item.description, value: item.code + '#' + item.termId });
            });
            setDiagnosisTermsAndCodes(result.json.items);
            setSearchHelper(true);
        } else if (!input || input.length <= 1) {
            if (!hasSelectedOption) {
                setDiagnosisTermsAndCodes([]);
            }

            setSearchHelper(false);
        }

        return options;
    }

    function cancelPreviousFetching() {
        abortableFetch.abort();
        abortableFetch = new AbortableFetch();
    }

    function getOptionsSecondDropdown() {
        const options: { label: string; value: string }[] = [];

        if (nestedOptions && nestedOptions.length > 0) {
            nestedOptions.forEach((item) => {
                options.push({
                    label: item.description,
                    value: item.code + '#' + item.termId,
                });
            });
        }

        return options;
    }

    function findSelectedByCodeAndTermId(selectedCode: string, selectedTermId: string) {
        const exactMatch = diagnosisTermsAndCodes.find(
            (item) => item.termId === selectedTermId && item.code === selectedCode
        );

        if (exactMatch) {
            return exactMatch;
        }

        return diagnosisTermsAndCodes.find(
            (item) =>
                (selectedTermId && item.termId === selectedTermId) ||
                (item.code === selectedCode && !item.termId) ||
                (item.thesaurusTerms &&
                    item.thesaurusTerms.some(
                        (nestedIten) => nestedIten.code === selectedCode && nestedIten.termId === selectedTermId
                    ))
        );
    }
}
